/**
 * StudentSecurityTab.jsx
 *
 * Kullanıcının güvenlik özetini gösterir:
 *   - Aktif oturumlar listesi + Tüm oturumları sonlandır
 *   - Son 30 gün anomaly geçmişi
 *   - Saatlik erişim pattern heatmap (bar chart)
 *
 * Props:
 *   userId: string — seçili kullanıcının _id'si
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Monitor, Smartphone, Globe, LogOut, AlertTriangle, UserCog, Link2, Send, Copy, Loader2 } from 'lucide-react';
import { EmptyState, LoadingState } from '../../components/admin';
import DangerousConfirmDialog from '@/components/admin/DangerousConfirmDialog';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { getApiErrorMessage } from '../../utils/apiError';
import axios from 'axios';
import {
  useActiveSessions,
  useRevokeUserSessions,
  useAnomalies,
  useUserAccessPattern,
} from '../../api/security';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Platform badge ────────────────────────────────────────────────────────────

function PlatformBadge({ platform }) {
  const icons = {
    ios: <Smartphone size={12} className="inline mr-1" />,
    android: <Smartphone size={12} className="inline mr-1" />,
    web: <Monitor size={12} className="inline mr-1" />,
  };
  const colors = {
    ios: 'bg-blue-50 text-blue-600',
    android: 'bg-green-50 text-green-600',
    web: 'bg-purple-50 text-purple-600',
  };
  const key = (platform || 'web').toLowerCase();
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold ${colors[key] || 'bg-gray-50 text-gray-500'}`}>
      {icons[key] || <Globe size={12} className="inline mr-1" />}
      {(platform || 'web').toUpperCase()}
    </span>
  );
}

// ── Severity badge ────────────────────────────────────────────────────────────

const SEVERITY_CONFIG = {
  low:    'bg-gray-100 text-gray-500',
  medium: 'bg-amber-50 text-amber-600',
  high:   'bg-rose-50 text-rose-600',
};

// ── AccessPatternChart ────────────────────────────────────────────────────────

function AccessPatternChart({ pattern = [], t }) {
  if (!pattern.length) {
    return (
      <p className="text-xs text-[var(--text-muted)] text-center py-3">
        {t('admin.security.userDetail.accessPattern.noData')}
      </p>
    );
  }

  const maxCount = Math.max(...pattern.map((p) => p.count), 1);

  return (
    <div>
      <div className="flex items-end gap-0.5 h-14">
        {Array.from({ length: 24 }, (_, hour) => {
          const entry = pattern.find((p) => p.hour === hour);
          const count = entry?.count || 0;
          const heightPct = Math.round((count / maxCount) * 100);
          return (
            <div key={hour} className="flex-1 flex flex-col items-center group relative">
              <div
                className="w-full bg-[var(--brand-primary)] rounded-sm opacity-60 group-hover:opacity-100 transition-opacity"
                style={{ height: `${Math.max(heightPct, count > 0 ? 4 : 0)}%` }}
              />
              {count > 0 && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 hidden group-hover:block bg-[#1C1C1C] text-white text-[8px] rounded px-1 py-0.5 whitespace-nowrap z-10">
                  {hour}:00 — {count}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-between mt-0.5 text-[8px] text-[var(--text-muted)]">
        <span>0</span><span>6</span><span>12</span><span>18</span><span>23</span>
      </div>
    </div>
  );
}

// ── ImpersonatePanel ──────────────────────────────────────────────────────────
/**
 * "Öğrenci Gibi Gir" ve "Giriş Linki Oluştur" butonları.
 * Impersonate → ticket → auto-login sayfası (yeni sekme).
 * Magic-link → kopyalanabilir URL + SMS gönderme opsiyonu.
 */
function ImpersonatePanel({ userId, lang, authHeaders }) {
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);

  const [impersonating, setImpersonating] = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicResult, setMagicResult] = useState(null); // { magic_link_url, expires_in }
  const [smsSending, setSmsSending] = useState(false);
  const [smsSent, setSmsSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleImpersonate = async () => {
    setImpersonating(true);
    try {
      const r = await axios.post(
        `${API}/admin/students/${userId}/impersonate`,
        {},
        { headers: authHeaders() }
      );
      const { ticket } = r.data;
      const url = `${window.location.origin}/auto-login?ticket=${encodeURIComponent(ticket)}`;
      window.open(url, '_blank', 'noopener');
      toast.success(tr('Öğrenci paneli yeni sekmede açıldı', 'Student panel opened in new tab'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setImpersonating(false);
    }
  };

  const handleMagicLink = async () => {
    setMagicLoading(true);
    setMagicResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/students/${userId}/magic-link`,
        {},
        { headers: authHeaders() }
      );
      setMagicResult(r.data);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setMagicLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!magicResult?.magic_link_url) return;
    try { await navigator.clipboard.writeText(magicResult.magic_link_url); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
    toast.success(tr('Link kopyalandı', 'Link copied'));
  };

  const handleSendSms = async () => {
    setSmsSending(true);
    try {
      await axios.post(
        `${API}/admin/students/${userId}/magic-link`,
        { send_sms: true },
        { headers: authHeaders() }
      );
      setSmsSent(true);
      toast.success(tr('Giriş linki SMS ile gönderildi', 'Login link sent via SMS'));
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setSmsSending(false);
    }
  };

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-bold text-[var(--text-main)] flex items-center gap-1.5">
        <UserCog size={15} className="text-amber-500" />
        {tr('Yönetici Erişim Araçları', 'Admin Access Tools')}
      </h3>

      <div className="flex flex-wrap gap-2">
        {/* Öğrenci Gibi Gir */}
        <motion.button
          type="button"
          onClick={handleImpersonate}
          disabled={impersonating}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-amber-300 text-amber-700 text-xs font-bold hover:bg-amber-50 disabled:opacity-50"
        >
          {impersonating ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <UserCog size={12} />
          )}
          {tr('Öğrenci Gibi Gir', 'Impersonate Student')}
        </motion.button>

        {/* Giriş Linki Oluştur */}
        <motion.button
          type="button"
          onClick={handleMagicLink}
          disabled={magicLoading}
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border-2 border-[var(--brand-primary)] text-[var(--brand-primary)] text-xs font-bold hover:bg-cyan-50 disabled:opacity-50"
        >
          {magicLoading ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Link2 size={12} />
          )}
          {tr('Giriş Linki Oluştur', 'Create Login Link')}
        </motion.button>
      </div>

      {/* Magic link result */}
      {magicResult && (
        <motion.div
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-3 rounded-xl border-2 border-[var(--border-light)] bg-white space-y-2"
        >
          <div className="flex items-center gap-2">
            <p className="text-[10px] text-[var(--text-muted)] font-mono flex-1 truncate">
              {magicResult.magic_link_url}
            </p>
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded border border-[var(--border-light)] text-[10px] font-bold hover:bg-gray-50"
            >
              <Copy size={10} className={copied ? 'text-emerald-500' : ''} />
              {copied ? tr('Kopyalandı', 'Copied') : tr('Kopyala', 'Copy')}
            </button>
          </div>

          {magicResult.expires_in && (
            <p className="text-[10px] text-[var(--text-muted)]">
              {tr('Geçerlilik:', 'Valid for:')} {magicResult.expires_in}
            </p>
          )}

          <button
            type="button"
            onClick={handleSendSms}
            disabled={smsSending || smsSent}
            className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 hover:underline disabled:opacity-50"
          >
            {smsSending ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Send size={11} />
            )}
            {smsSent
              ? tr('SMS Gönderildi ✓', 'SMS Sent ✓')
              : tr('SMS ile Gönder', 'Send via SMS')}
          </button>
        </motion.div>
      )}
    </section>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function StudentSecurityTab({ userId }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);

  // Sessions (filtered by user)
  // Backend /admin/sessions accepts a user_id param for per-user view
  const { data: sessionsData, isLoading: sessionsLoading } = useActiveSessions(
    userId ? { search: '', platform: '', ip_country: '', page: 0, limit: 10 } : {}
  );
  // We filter client-side by user_id since the API may not have per-user sessions endpoint yet
  const allSessions = sessionsData?.sessions || [];
  const sessions = allSessions.filter((s) => !userId || s.user_id === userId);

  // Anomalies for this user
  const { data: anomalyData, isLoading: anomalyLoading } = useAnomalies(
    userId ? { search: userId, status: '', severity: '', rule: '', page: 0, limit: 10 } : {}
  );
  const anomalies = anomalyData?.anomalies || [];

  // Access pattern
  const { data: patternData, isLoading: patternLoading } = useUserAccessPattern(userId);
  const pattern = patternData?.pattern || [];

  // Revoke mutation
  const revokeAll = useRevokeUserSessions();

  const handleRevokeConfirm = async () => {
    try {
      await revokeAll.mutateAsync(userId);
      toast.success(
        lang === 'tr' ? 'Tüm oturumlar sonlandırıldı' : 'All sessions revoked'
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setRevokeDialogOpen(false);
    }
  };

  if (!userId) {
    return (
      <EmptyState
        title={t('admin.studentHub.select_user')}
        icon={<Shield size={40} strokeWidth={1.5} />}
      />
    );
  }

  return (
    <div className="space-y-5">
      {/* ── Admin access tools ── */}
      <ImpersonatePanel userId={userId} lang={lang} authHeaders={authHeaders} />

      {/* ── Active sessions ── */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-bold text-[var(--text-main)] flex items-center gap-1.5">
            <Shield size={15} className="text-[var(--brand-primary)]" />
            {t('admin.security.activeSessions.title')}
          </h3>
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setRevokeDialogOpen(true)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border-2 border-rose-200 text-rose-500 text-[11px] font-bold hover:bg-rose-50 transition-colors"
          >
            <LogOut size={12} />
            {t('admin.security.activeSessions.actionRevoke')}
          </motion.button>
        </div>

        {sessionsLoading ? (
          <LoadingState message="" />
        ) : sessions.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] py-2">
            {t('admin.security.activeSessions.emptyTitle')}
          </p>
        ) : (
          <div className="space-y-1.5">
            {sessions.map((s, i) => (
              <div
                key={s.session_id || s._id || i}
                className="flex items-center justify-between gap-2 p-2 rounded-xl border-2 border-[var(--border-light)] bg-white"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <PlatformBadge platform={s.platform} />
                  <span className="text-xs text-[var(--text-main)] truncate">{s.device_name || '—'}</span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="font-mono text-[10px] text-[var(--text-muted)]">
                    {s.ip_address || '—'}
                    {s.ip_country ? ` [${s.ip_country}]` : ''}
                  </span>
                  <span className="text-[10px] text-[var(--text-muted)]">
                    {s.last_seen_at
                      ? new Date(s.last_seen_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')
                      : '—'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ── Access pattern ── */}
      <section className="p-3 border-2 border-[var(--border-light)] rounded-xl">
        <h3 className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-2">
          {t('admin.security.userDetail.accessPattern.title')}
        </h3>
        {patternLoading ? (
          <p className="text-xs text-[var(--text-muted)] text-center py-3">
            {lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}
          </p>
        ) : (
          <AccessPatternChart pattern={pattern} t={t} />
        )}
      </section>

      {/* ── Anomaly history ── */}
      <section>
        <h3 className="text-sm font-bold text-[var(--text-main)] flex items-center gap-1.5 mb-2">
          <AlertTriangle size={15} className="text-amber-500" />
          {lang === 'tr' ? 'Son 30 Gün Anomaly Geçmişi' : 'Last 30 Days Anomaly History'}
        </h3>

        {anomalyLoading ? (
          <LoadingState message="" />
        ) : anomalies.length === 0 ? (
          <p className="text-xs text-[var(--text-muted)] py-2">
            {t('admin.security.anomalies.emptyTitle')}
          </p>
        ) : (
          <div className="space-y-1.5">
            {anomalies.map((a, i) => (
              <div
                key={a._id || a.id || i}
                className="flex items-center justify-between gap-2 p-2 rounded-xl border-2 border-[var(--border-light)] bg-white"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold ${SEVERITY_CONFIG[a.severity] || 'bg-gray-100 text-gray-500'}`}>
                    {t(`admin.security.anomalies.severity.${a.severity}`) || a.severity}
                  </span>
                  <span className="text-xs text-[var(--text-main)] truncate">
                    {t(`admin.security.anomalies.rule.${a.rule}`) || a.rule}
                  </span>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] text-[var(--text-muted)]">
                    {a.created_at
                      ? new Date(a.created_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')
                      : '—'}
                  </span>
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold ${
                    a.status === 'open' ? 'bg-rose-50 text-rose-600'
                    : a.status === 'reviewed' ? 'bg-blue-50 text-blue-600'
                    : 'bg-gray-100 text-gray-400'
                  }`}>
                    {t(`admin.security.anomalies.status.${a.status}`) || a.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 2-step revoke confirm */}
      <DangerousConfirmDialog
        open={revokeDialogOpen}
        onOpenChange={setRevokeDialogOpen}
        count={1}
        onConfirm={handleRevokeConfirm}
        loading={revokeAll.isPending}
      />
    </div>
  );
}
